import { Stack, SxProps, Theme } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { AriaAttributes, AriaRole, forwardRef, ReactNode } from 'react';

const styleContainer = (maxWidth: number) => ({
    width: '100%',
    maxWidth: `min(${maxWidth}px, 100%)`,
    mx: 'auto',
});

/**
 * Columns
 * Will move these to design tokens when finalized
 *
 */
export type LayoutColumns = 1 | 2 | 4 | 6 | 8 | 10 | 12;

export const gutterWidth = 24;
export const columnWidth = 93;

/**
 * 1 = 93px
 * 2 = 210px
 * 4 = 444px
 * 6 = 678px
 * 8 = 912px
 * 10 = 1146px
 * 12 = 1380px
 */
export function columnsToWidth(columns: LayoutColumns) {
    return columns * columnWidth + (columns - 1) * gutterWidth;
}

export const maxWidths: Record<LayoutColumns, number> = {
    /** 93px */
    1: columnsToWidth(1),
    /**  210px */
    2: columnsToWidth(2),
    /** 444px */
    4: columnsToWidth(4), // TODO Replace style values of 440 and 442 with this
    /** 678px */
    6: columnsToWidth(6),
    /** 912px */
    8: columnsToWidth(8),
    /** 1146px */
    10: columnsToWidth(10),
    /** 1380 */
    12: columnsToWidth(12),
};

/** End Columns */

interface MaxColumnsContainerProps {
    children?: ReactNode;
    id?: string;
    sx?: SxProps<Theme>;
    gap?: number;
    columns: LayoutColumns;
    role?: AriaRole;
    'aria-live'?: AriaAttributes['aria-live'];
}

/**
 * Creates an element which is contrained to a maximum width equal to a number of columns of a 1440px layout.
 *
 * @param {Object} props
 * @param {number} props.columns - Set max width equal to the width of this number of columns, within a 1440px grid
 */
export const MaxColumnsContainer = forwardRef<
    HTMLDivElement,
    MaxColumnsContainerProps
>(function MaxColumnsContainer(
    { children, sx, id, columns, gap = 0, role, 'aria-live': ariaLive },
    ref
) {
    const maxWidth = maxWidths[columns];

    return (
        <Stack
            ref={ref}
            gap={gap}
            sx={sxAppend(styleContainer(maxWidth), sx)}
            id={id}
            role={role}
            aria-live={ariaLive}
        >
            {children}
        </Stack>
    );
});
